/** Minimal rotation math so `core` stays framework-agnostic (no three.js dependency). */

export interface Vec3 {
  x: number;
  y: number;
  z: number;
}

export interface Quat {
  x: number;
  y: number;
  z: number;
  w: number;
}

/** Device orientation event angles, in degrees (W3C DeviceOrientationEvent). */
export interface DeviceOrientationAngles {
  alpha: number;
  beta: number;
  gamma: number;
}

const DEG2RAD = Math.PI / 180;

export function quatNormalize(q: Quat): Quat {
  const len = Math.hypot(q.x, q.y, q.z, q.w);
  if (len === 0) return { x: 0, y: 0, z: 0, w: 1 };
  return { x: q.x / len, y: q.y / len, z: q.z / len, w: q.w / len };
}

/** Hamilton product a·b (apply b, then a). */
export function quatMultiply(a: Quat, b: Quat): Quat {
  return {
    x: a.w * b.x + a.x * b.w + a.y * b.z - a.z * b.y,
    y: a.w * b.y - a.x * b.z + a.y * b.w + a.z * b.x,
    z: a.w * b.z + a.x * b.y - a.y * b.x + a.z * b.w,
    w: a.w * b.w - a.x * b.x - a.y * b.y - a.z * b.z,
  };
}

/** Quaternion for a rotation of `angle` radians about a (unit) axis. */
export function quatFromAxisAngle(axis: Vec3, angle: number): Quat {
  const half = angle / 2;
  const s = Math.sin(half);
  return { x: axis.x * s, y: axis.y * s, z: axis.z * s, w: Math.cos(half) };
}

/** Quaternion from intrinsic Euler angles (radians) applied in three.js 'YXZ' order. */
export function quatFromEulerYXZ(x: number, y: number, z: number): Quat {
  const c1 = Math.cos(x / 2);
  const c2 = Math.cos(y / 2);
  const c3 = Math.cos(z / 2);
  const s1 = Math.sin(x / 2);
  const s2 = Math.sin(y / 2);
  const s3 = Math.sin(z / 2);
  return {
    x: s1 * c2 * c3 + c1 * s2 * s3,
    y: c1 * s2 * c3 - s1 * c2 * s3,
    z: c1 * c2 * s3 - s1 * s2 * c3,
    w: c1 * c2 * c3 + s1 * s2 * s3,
  };
}

/** Rotate vector `v` by quaternion `q` (v' = q · v · q⁻¹, computed without the inverse). */
export function applyQuat(q: Quat, v: Vec3): Vec3 {
  const tx = 2 * (q.y * v.z - q.z * v.y);
  const ty = 2 * (q.z * v.x - q.x * v.z);
  const tz = 2 * (q.x * v.y - q.y * v.x);
  return {
    x: v.x + q.w * tx + (q.y * tz - q.z * ty),
    y: v.y + q.w * ty + (q.z * tx - q.x * tz),
    z: v.z + q.w * tz + (q.x * ty - q.y * tx),
  };
}

// Base orientation: a flat phone (screen up) should look at the horizon, so the camera is
// rotated −90° about X. Matches three.js DeviceOrientationControls.
const SCREEN_TO_WORLD = quatFromAxisAngle({ x: 1, y: 0, z: 0 }, -Math.PI / 2);

/**
 * Map a DeviceOrientationEvent (alpha/beta/gamma, degrees) to a camera quaternion in a Y-up
 * world, accounting for the current screen orientation. This is the canonical three.js mapping.
 */
export function deviceOrientationToQuaternion(
  angles: DeviceOrientationAngles,
  screenOrientationDeg = 0,
): Quat {
  const alpha = angles.alpha * DEG2RAD;
  const beta = angles.beta * DEG2RAD;
  const gamma = angles.gamma * DEG2RAD;

  let q = quatFromEulerYXZ(beta, alpha, -gamma);
  q = quatMultiply(q, SCREEN_TO_WORLD);
  const screenSpin = quatFromAxisAngle({ x: 0, y: 0, z: 1 }, -screenOrientationDeg * DEG2RAD);
  q = quatMultiply(q, screenSpin);
  return q;
}
